import type { SchemesRecord } from '../types';

import { varAlpha } from 'minimal-shared/utils';

import {
  grey,
  info,
  error,
  common,
  primary,
  success,
  warning,
  secondary,
} from './palette';

// ----------------------------------------------------------------------

/**
 * TypeScript extension for MUI theme augmentation.
 * @to {@link file://./../extend-theme-types.d.ts}
 */

export type CustomShadows = {
  z1: string;
  z4: string;
  z8: string;
  z12: string;
  z16: string;
  z20: string;
  z24: string;
  primary: string;
  secondary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
  card: string;
  dialog: string;
  dropdown: string;
  // Tailwind/Figma shadow tokens
  shadow2xs: string;
  shadowXs: string;
  shadowSm: string;
  shadowMd: string;
  shadowLg: string;
  shadowXl: string;
  shadow2xl: string;
};

// ----------------------------------------------------------------------

export function createShadowColor(colorChannel: string): string {
  return `0 8px 16px 0 ${varAlpha(colorChannel, 0.24)}`;
}

function createCustomShadows(colorChannel: string): CustomShadows {
  return {
    z1: `0 1px 2px 0 ${varAlpha(colorChannel, 0.16)}`,
    z4: `0 4px 8px 0 ${varAlpha(colorChannel, 0.16)}`,
    z8: `0 8px 16px 0 ${varAlpha(colorChannel, 0.16)}`,
    z12: `0 12px 24px -4px ${varAlpha(colorChannel, 0.16)}`,
    z16: `0 16px 32px -4px ${varAlpha(colorChannel, 0.16)}`,
    z20: `0 20px 40px -4px ${varAlpha(colorChannel, 0.16)}`,
    z24: `0 24px 48px 0 ${varAlpha(colorChannel, 0.16)}`,
    /********/
    dialog: `0 20px 25px -5px ${varAlpha(
      colorChannel,
      0.1
    )}, 0 8px 10px -6px ${varAlpha(colorChannel, 0.1)}`,
    card: `0 1px 3px 0 ${varAlpha(
      colorChannel,
      0.1
    )}, 0 1px 2px -1px ${varAlpha(colorChannel, 0.1)}`,
    dropdown: `0 10px 15px -3px ${varAlpha(
      colorChannel,
      0.1
    )}, 0 4px 6px -4px ${varAlpha(colorChannel, 0.1)}`,
    /********/
    primary: createShadowColor(primary.mainChannel),
    secondary: createShadowColor(secondary.mainChannel),
    info: createShadowColor(info.mainChannel),
    success: createShadowColor(success.mainChannel),
    warning: createShadowColor(warning.mainChannel),
    error: createShadowColor(error.mainChannel),
    // Tailwind/Figma shadow tokens
    shadow2xs: `0 1px 0 0 ${varAlpha(colorChannel, 0.05)}`,
    shadowXs: `0 1px 2px 0 ${varAlpha(colorChannel, 0.05)}`,
    shadowSm: `0 1px 3px 0 ${varAlpha(
      colorChannel,
      0.1
    )}, 0 1px 2px -1px ${varAlpha(colorChannel, 0.1)}`,
    shadowMd: `0 4px 6px -1px ${varAlpha(
      colorChannel,
      0.1
    )}, 0 2px 4px -2px ${varAlpha(colorChannel, 0.1)}`,
    shadowLg: `0 10px 15px -3px ${varAlpha(
      colorChannel,
      0.1
    )}, 0 4px 6px -4px ${varAlpha(colorChannel, 0.1)}`,
    shadowXl: `0 20px 25px -5px ${varAlpha(
      colorChannel,
      0.1
    )}, 0 8px 10px -6px ${varAlpha(colorChannel, 0.1)}`,
    shadow2xl: `0 25px 50px 12px ${varAlpha(colorChannel, 0.25)}`,
  };
}

/* **********************************************************************
 * 📦 Final
 * **********************************************************************/
export const customShadows: SchemesRecord<CustomShadows> = {
  light: createCustomShadows(grey['600Channel']),
  dark: createCustomShadows(common.blackChannel),
};
